import React, { useRef, useEffect } from 'react'
import { CameraEnhancer, CameraView } from 'dynamsoft-camera-enhancer'
import { CaptureVisionRouter } from 'dynamsoft-capture-vision-router'
import { MultiFrameResultCrossFilter } from 'dynamsoft-utility'
import { EnumBarcodeFormat } from 'dynamsoft-barcode-reader-bundle'
import './VideoCapture.css'
import '../../dynamsoft.config'

const VideoCapture = ({ onBarcodeScanned }) => {
  const cameraViewContainer = useRef(null)
  const cameraEnhancerRef = useRef(null)
  const cvRouterRef = useRef(null)
  const isStoppedRef = useRef(false)

  useEffect(() => {
    let isMounted = true

    const setupCamera = async () => {
      if (cameraEnhancerRef.current || cvRouterRef.current) {
        console.log('Camera already initialized.')
        return
      }

      try {
        // Create CameraView instance
        const cameraView = await CameraView.createInstance()

        // Attach CameraView to DOM
        if (isMounted && cameraViewContainer.current) {
          cameraViewContainer.current.append(cameraView.getUIElement())
        }

        // Create CameraEnhancer
        const cameraEnhancer = await CameraEnhancer.createInstance(cameraView)
        cameraEnhancerRef.current = cameraEnhancer

        // Create CaptureVisionRouter
        const cvRouter = await CaptureVisionRouter.createInstance()
        cvRouterRef.current = cvRouter
        cvRouter.setInput(cameraEnhancer)

        // Add result callback
        cvRouter.addResultReceiver({
          onDecodedBarcodesReceived: async (result) => {
            if (isStoppedRef.current || !result.barcodeResultItems.length) return

            const barcodeResults = result.barcodeResultItems.map((item) => ({
              format: item.formatString,
              text: item.text
            }))

            console.log('Scanned Barcodes:', barcodeResults)

            isStoppedRef.current = true
            if (cvRouterRef.current) await cvRouterRef.current.stopCapturing()
            if (cameraEnhancerRef.current) await cameraEnhancerRef.current.close()

            if (onBarcodeScanned) {
              onBarcodeScanned(barcodeResults)
            }
          }
        })

        // Add result filters
        const filter = new MultiFrameResultCrossFilter()
        filter.enableResultCrossVerification('barcode', true)
        filter.enableResultDeduplication('barcode', true)
        await cvRouter.addResultFilter(filter)

        // Open camera and start scanning
        await cameraEnhancer.open()
        cameraView.setScanLaserVisible(true)

        const cvSettings = await cvRouter.getSimplifiedSettings('ReadSingleBarcode')
        cvSettings.barcodeSettings.barcodeFormatIds = EnumBarcodeFormat.BF_ONED
        await cvRouter.updateSettings('ReadSingleBarcode', cvSettings)

        await cvRouter.startCapturing('ReadSingleBarcode')
      } catch (error) {
        console.error('Error during camera setup:', error.message || error)
      }
    }

    setupCamera()

    return () => {
      isMounted = false
      cvRouterRef.current?.dispose()
      cameraEnhancerRef.current?.dispose()
      cvRouterRef.current = null
      cameraEnhancerRef.current = null
    }
  }, [onBarcodeScanned])

  // Account for the fixed footer height (adjust height to prevent overflow)
  const footerHeight = 60 // Height of the footer in pixels

  return (
    <div
      ref={cameraViewContainer}
      className="camera-view-container"
      style={{ width: '100%', height: `calc(100vh - ${footerHeight}px)` }}
    ></div>
  )
}

export default VideoCapture